/**
 * Push-campaign creation. The admin MAY write push_campaigns docs directly
 * (security rules: allow read, write if isAdmin()).
 *
 * FREE / Spark plan: there is NO send function. This panel only CREATES the
 * campaign doc (with `createdAt`, and `scheduledAt` when scheduled). Actual
 * delivery / FCM fan-out is performed OUT OF BAND by a separate service-account
 * push script (documented by the Lead) that reads these docs and sends. This
 * app never dispatches notifications itself.
 */
import {
  addDoc,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { COLLECTIONS, type PushAudience } from '@mitang/shared';
import { auth, db, storage } from '../lib/firebase';

/** Ask the worker to deliver queued campaigns right away (best-effort). */
async function triggerDelivery(): Promise<void> {
  const endpoint: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
  if (!endpoint) return;
  try {
    const idToken = await auth.currentUser?.getIdToken();
    await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'push_now', idToken }),
    });
  } catch {
    // Non-fatal: the every-minute worker pass will pick it up anyway.
  }
}

// Raw (un-converted) collection ref: writes stay plain objects so fields like
// `id` are not stripped by the read converter's toFirestore.
const rawPushCampaigns = collection(db, COLLECTIONS.pushCampaigns);

export interface NewCampaignInput {
  title: string;
  body: string;
  imageUrl?: string;
  deepLink?: string;
  audience: PushAudience;
  audienceValue?: string[] | string;
  scheduledAt?: number; // when set → scheduled (not sent immediately)
  createdBy: string; // admin uid
}

/**
 * Upload a campaign image to Storage under campaign_images/ (matches
 * storage.rules: admin-only write, <5MB, image/*). Returns a public URL.
 */
export async function uploadCampaignImage(file: File): Promise<string> {
  const safe = file.name.replace(/[^a-z0-9.]+/gi, '-').toLowerCase();
  const path = `campaign_images/${Date.now()}-${safe}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

/**
 * Create the push_campaigns doc. We let Firestore mint the id and mirror it
 * into the document body (PushCampaign.id) with a follow-up setDoc so the
 * shape matches the shared type. Returns the created id.
 */
export async function createCampaign(input: NewCampaignInput): Promise<string> {
  // Build the persisted shape. Omit undefined fields (Firestore rejects them).
  const base: Record<string, unknown> = {
    title: input.title,
    body: input.body,
    audience: input.audience,
    createdBy: input.createdBy,
    // createdAt is not in the shared PushCampaign type but is useful for
    // ordering history; kept as an extra server-stamped field.
    createdAt: serverTimestamp(),
  };
  if (input.imageUrl) base.imageUrl = input.imageUrl;
  if (input.deepLink) base.deepLink = input.deepLink;
  if (input.audienceValue !== undefined) base.audienceValue = input.audienceValue;
  if (input.scheduledAt !== undefined) base.scheduledAt = input.scheduledAt;

  const created = await addDoc(rawPushCampaigns, base);
  // Mirror the id into the doc body for the shared PushCampaign shape.
  await setDoc(doc(rawPushCampaigns, created.id), { id: created.id }, { merge: true });
  // Scheduled campaigns wait for their time; immediate ones go out now.
  if (input.scheduledAt === undefined) await triggerDelivery();
  return created.id;
}

/** Coerce a Firestore Timestamp | number | undefined into epoch ms. */
export function tsToMs(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  if (value instanceof Timestamp) return value.toMillis();
  return undefined;
}
